// src/app/core/services/sync-coordinator.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Subject, timer, BehaviorSubject, Observable, merge, fromEvent } from 'rxjs';
import { takeUntil, switchMap, tap, catchError, filter, take, skip, throttleTime } from 'rxjs/operators';
import { of } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SettingsStaleSignal } from './settings-stale.signal';
import { skippedTicksAfterErrors } from './sync-backoff';
import { isTerminalSession } from './session/terminal-session';
import { CompanySettingsService } from '../../modules/settings/services/company-settings.service';
import { UserPermissionsService } from '../../modules/employee/services/user-permissions.service';
import { HttpService } from './http/http.service';
import { ApiEndPoints, ApiMethod } from './const';
import { CompanySettings } from '../../modules/settings/models/company-settings.model';
import { UserPermissionConfig } from '../../modules/employee/model/user-permissions.model';
import { AppStateService } from './state/app-state.service';
import { AuthService } from '../../modules/auth/services/auth/auth.service';

// Interfaces mit PascalCase wie vom Backend
export interface SyncCheckResponse {
  HasChanges: boolean;
  Changes: number[];
  ServerTime: string;
  LastCheckUtc?: string;
  FromCache?: boolean;
}

export interface SyncConfigurationRequest {
  RequestCompanySettings: boolean;
  RequestUserPermissions: boolean;
}

export interface SyncConfigurationResponse {
  CompanySettings?: CompanySettings;
  CompanySettingsVersion?: string;
  UserPermissions?: UserPermissionConfig;
  UserPermissionsVersion?: string;
}

// Backend ChangeType enum values
export enum ChangeTypeValue {
  CompanySettings = 1,
  UserPermissions = 2,
}

export enum ChangeType {
  CompanySettings = 'CompanySettings',
  UserPermissions = 'UserPermissions',
}

@Injectable({
  providedIn: 'root',
})
export class SyncCoordinatorService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private isActiveSubject = new BehaviorSubject<boolean>(false);
  private lastCheck = new Date();

  // Status tracking
  private isSyncingSubject = new BehaviorSubject<boolean>(false);
  private lastSyncTimeSubject = new BehaviorSubject<Date | null>(null);

  // Polling Konfiguration
  private readonly POLL_INTERVAL = 30000; // 30 Sekunden
  /** Terminal: nur jeder zweite Takt, also alle 60 Sekunden (siehe `isRegularTick`). */
  private readonly TERMINAL_TICK_EVERY = 2;
  private readonly SYNC_CHECK_ENDPOINT = '/Sync/check';

  // Error tracking
  private consecutiveErrors = 0;
  private readonly MAX_ERRORS = 3;
  /** Takte, die nach Fehlern noch übersprungen werden (siehe `skippedTicksAfterErrors`). */
  private skipTicks = 0;

  /** Außerplanmäßige Prüfung: App/Tab wieder sichtbar, Netz zurück. */
  private readonly wake$ = new Subject<void>();
  /** Beendet die laufende Abfrage; ein erneutes `start()` legt sonst eine zweite daneben. */
  private readonly stop$ = new Subject<void>();

  // Debug mode
  private debugMode = false;

  constructor(
    private httpService: HttpService,
    private companySettingsService: CompanySettingsService,
    private userPermissionsService: UserPermissionsService,
    private appState: AppStateService,
    private authService: AuthService,
    private settingsStale: SettingsStaleSignal
  ) {
    // Debug mode aus localStorage
    this.debugMode = localStorage.getItem('syncDebugMode') === 'true';

    // Der Server hat etwas abgelehnt, das die Oberfläche nach ihren Einstellungen noch anbietet:
    // sofort nachladen, statt auf eine Änderungsmeldung zu warten. Gedrosselt, weil ein Nutzer
    // denselben Knopf mehrmals drückt.
    this.settingsStale.stale$
      .pipe(throttleTime(10_000), takeUntil(this.destroy$))
      .subscribe(() => {
        if (localStorage.getItem('token')) {
          this.processChanges([ChangeTypeValue.CompanySettings]);
        }
      });

    this.listenForWakeUps();
  }

  /**
   * Startet den Sync-Check
   */
  start(): void {
    if (this.isActiveSubject.value) {
      return;
    }

    // Prüfe ob Token vorhanden ist
    if (!localStorage.getItem('token')) {
      return;
    }

    this.stop$.next();
    this.isActiveSubject.next(true);
    this.lastCheck = new Date();
    this.consecutiveErrors = 0;
    this.skipTicks = 0;

    // Fester Takt plus außerplanmäßige Prüfungen beim Zurückkehren (Hintergrund-Tabs und
    // pausierte Apps bekommen ihre Timer gedrosselt oder gar nicht).
    merge(
      timer(0, this.POLL_INTERVAL).pipe(filter((tick) => this.isRegularTick(tick))),
      this.wake$
    )
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.stop$),
        filter(
          () => this.isActiveSubject.value && !this.isSyncingSubject.value
        ),
        filter(() => this.consumeTick()),
        switchMap(() => this.performCheck()),
        catchError((error) => this.handleError(error))
      )
      .subscribe();
  }

  /**
   * Stoppt den Sync-Check
   */
  stop(): void {
    this.isActiveSubject.next(false);
    this.consecutiveErrors = 0;
    this.skipTicks = 0;
    this.stop$.next();
  }

  /**
   * Sofort prüfen, unabhängig vom Takt und von einer Wartezeit nach Fehlern.
   * Ohne laufende Synchronisation (abgemeldet) passiert nichts.
   */
  checkNow(): void {
    this.skipTicks = 0;
    this.wake$.next();
  }

  /**
   * Ein Terminal prüft nur jeden zweiten Takt (alle 60 s). Der Abruf ist dort vor allem
   * Herzschlag, und „online“ gilt in der Terminal-Verwaltung bis 5 Minuten nach dem letzten
   * (`getTerminalStatus`) — fünf Herzschläge Puffer. Außerplanmäßige Prüfungen (`wake$`)
   * bleiben ungedrosselt. Gelesen wird bei jedem Takt, ein Wechsel der Sitzung greift sofort.
   */
  private isRegularTick(tick: number): boolean {
    return !isTerminalSession() || tick % this.TERMINAL_TICK_EVERY === 0;
  }

  /** Nach Fehlern nur jeden n-ten Takt prüfen; ein außerplanmäßiger Anlass setzt das zurück. */
  private consumeTick(): boolean {
    if (this.skipTicks > 0) {
      this.skipTicks--;
      return false;
    }
    return true;
  }

  /**
   * Prüft, sobald das Gerät wieder da ist. Bis 12.10.2026 kam die nächste Prüfung erst mit dem
   * nächsten Takt — und in einem Hintergrund-Tab oder einer pausierten App womöglich gar nicht.
   */
  private listenForWakeUps(): void {
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(
          filter(() => document.visibilityState === 'visible'),
          takeUntil(this.destroy$)
        )
        .subscribe(() => this.checkNow());
    }
    if (typeof window !== 'undefined') {
      fromEvent(window, 'online')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.checkNow());
    }
    if (Capacitor.isNativePlatform()) {
      App.addListener('resume', () => this.checkNow());
    }
  }

  /**
   * Aktiviert/Deaktiviert Debug Mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    localStorage.setItem('syncDebugMode', enabled.toString());
  }

  /**
   * Führt einen Check aus und ruft bei Bedarf die Service-Methoden auf
   */
  private performCheck(): Observable<any> {
    // Prüfe ob Token noch vorhanden ist
    if (!localStorage.getItem('token')) {
      this.stop();
      return of(null);
    }

    // GET Request mit Query Parameter
    const params = new HttpParams().set(
      'lastCheck',
      this.lastCheck.toISOString()
    );

    return this.httpService
      .requestCall(
        this.SYNC_CHECK_ENDPOINT,
        ApiMethod.GET,
        null, // Kein Body bei GET
        params // Query Parameter als HttpParams
      )
      .pipe(
        tap((response: SyncCheckResponse) => {
          // Update lastCheck mit ServerTime (PascalCase!)
          this.lastCheck = new Date(
            response.ServerTime || new Date().toISOString()
          );
          this.consecutiveErrors = 0; // Reset error counter on success
          this.skipTicks = 0;

          // Verwende PascalCase Properties
          if (response.HasChanges && response.Changes?.length > 0) {
            this.processChanges(response.Changes);
          }
        }),
        catchError((error) => {
          return this.handleError(error);
        })
      );
  }

  /**
   * Verarbeitet die erkannten Änderungen und ruft die entsprechenden Service-Methoden auf
   */
  private processChanges(changes: number[]): void {
    // Verhindere mehrfache gleichzeitige Syncs
    if (this.isSyncingSubject.value) {
      return;
    }

    this.isSyncingSubject.next(true);

    // Prüfe welche Updates benötigt werden
    const needsCompanySettingsUpdate = changes.some(
      (change) => change === ChangeTypeValue.CompanySettings
    );
    const needsUserPermissionsUpdate = changes.some(
      (change) => change === ChangeTypeValue.UserPermissions
    );

    // Rufe die Service-Methoden auf und warte auf Completion
    try {
      const promises: Promise<void>[] = [];

      if (needsCompanySettingsUpdate) {
        const promise = this.updateCompanySettingsAsync();
        promises.push(promise);
      }

      if (needsUserPermissionsUpdate) {
        // WICHTIG: Terminals haben keine UserPermissions (kein EmployeeId)!
        // Überspringe UserPermissions-Sync für Terminals
        if (!this.authService.isTerminal) {
          // CRITICAL: Cache clearen BEVOR refresh (verhindert stale cached data)
          // Dies cleared sowohl AppStateService.permissionsCache$ als auch UserPermissionsService.sessionStorage
          this.appState.clearPermissions();
          this.userPermissionsService.clearPermissions();
          this.updateUserPermissions();
        }
      }

      // Warte bis alle Updates fertig sind, dann emitte Event
      Promise.all(promises).then(() => {
        this.lastSyncTimeSubject.next(new Date());

        // WICHTIG: Event erst NACH dem Laden emittieren!
        this.emitGlobalSyncEvent(changes);

        // Reset sync flag nach kurzer Verzögerung
        setTimeout(() => {
          this.isSyncingSubject.next(false);
        }, 500);
      }).catch((error) => {
        console.error('SyncCoordinator: Error waiting for updates', error);
        // Auch bei Fehler Event emittieren (fallback)
        this.emitGlobalSyncEvent(changes);
        setTimeout(() => {
          this.isSyncingSubject.next(false);
        }, 500);
      });
    } catch (error) {
      console.error('SyncCoordinator: Error processing changes', error);
      setTimeout(() => {
        this.isSyncingSubject.next(false);
      }, 500);
    }
  }

  /**
   * Aktualisiert Company Settings über den bestehenden Service (LEGACY)
   */
  private updateCompanySettings(): void {
    // Nutze die Methode mit skipBackgroundRefresh = true
    // Dies verhindert den doppelten API-Call
    this.companySettingsService.getCompanySettings(true);
  }

  /**
   * Aktualisiert Company Settings ASYNC und wartet auf Completion
   * WICHTIG: Promise resolved erst NACH erfolgreichem Laden vom Server!
   *
   * Race Condition Fix (behoben mit korrekter Reihenfolge):
   * Problem: HTTP-Response kann so schnell sein (Millisekunden), dass sie FERTIG ist,
   * BEVOR wir zum Observable subscriben. Dann gibt es kein emit mehr → Timeout.
   *
   * Lösung: ZUERST subscriben, DANN forceRefreshFromServer aufrufen!
   * - BehaviorSubject emittiert SOFORT seinen letzten (cached) Wert beim Subscribe
   * - skip(1) überspringt diesen cached Wert
   * - DANN rufen wir forceRefreshFromServer() auf
   * - take(1) nimmt das NÄCHSTE emit (vom Server-Response)
   * - Promise resolves mit frischen Settings vom Server
   *
   * Korrekte Reihenfolge:
   * 1. Subscribe zum Observable mit skip(1) + take(1)
   * 2. forceRefreshFromServer() - triggert HTTP-Call
   * 3. Server-Response kommt → BehaviorSubject.next() emittiert
   * 4. take(1) empfängt das Server-emit
   * 5. Promise resolves mit frischen Settings
   */
  private updateCompanySettingsAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Timeout fallback Variable
      let timeoutId: any;
      let subscription: any;

      // WICHTIG: Subscribe ZUERST, BEVOR forceRefreshFromServer aufgerufen wird!
      // Grund: HTTP-Response kann so schnell sein, dass sie vor dem Subscribe fertig ist
      subscription = this.companySettingsService.CompanySettingsObservable
        .pipe(
          skip(1), // Überspringt den cached Value, der sofort beim Subscribe emittiert wird
          take(1), // Nimmt das NÄCHSTE emit (vom Server-Response)
          filter(settings => settings !== null && settings !== undefined)
        )
        .subscribe({
          next: (settings) => {
            clearTimeout(timeoutId); // Clear timeout bei Erfolg
            resolve();
          },
          error: (error) => {
            console.error('SyncCoordinator: Failed to load CompanySettings', error);
            clearTimeout(timeoutId);
            reject(error);
          }
        });

      // JETZT forceRefreshFromServer aufrufen - NACH dem Subscribe!
      // So ist garantiert, dass wir das emit vom Server empfangen
      this.companySettingsService.forceRefreshFromServer();

      // Timeout fallback: Nach 5 Sekunden resolven, auch wenn nichts kommt
      timeoutId = setTimeout(() => {
        console.warn('SyncCoordinator: CompanySettings load timeout, resolving anyway');
        if (subscription) {
          subscription.unsubscribe();
        }
        resolve();
      }, 5000);
    });
  }

  /**
   * Aktualisiert User Permissions über den bestehenden Service
   * WICHTIG: skipBackgroundRefresh = true, da wir bereits vom Sync kommen
   */
  private updateUserPermissions(): void {
    // Nutze die überarbeitete Methode mit skipBackgroundRefresh = true
    // Dies verhindert den doppelten API-Call
    this.userPermissionsService.getCurrentUserPermissions(true);
  }

  /**
   * Holt Configuration Updates über API - falls ein separater Endpoint existiert
   */
  syncConfigurations(
    types: ChangeType[]
  ): Observable<SyncConfigurationResponse> {
    // Dieser Endpoint müsste im Backend implementiert werden
    const request: SyncConfigurationRequest = {
      RequestCompanySettings: types.includes(ChangeType.CompanySettings),
      RequestUserPermissions: types.includes(ChangeType.UserPermissions),
    };

    return this.httpService
      .requestCall(
        ApiEndPoints.SyncConfigurations, // Dieser Endpoint müsste im Backend existieren
        ApiMethod.POST,
        request
      )
      .pipe(
        tap((response: SyncConfigurationResponse) => {
          if (response.CompanySettings) {
            const settings = CompanySettings.fromStorage(
              response.CompanySettings
            );

            this.companySettingsService.CompanySettings = settings;
            localStorage.setItem('CompanySettings', JSON.stringify(settings));

            (this.companySettingsService as any).CompanySettingsSource.next(
              settings
            );
          }

          if (response.UserPermissions) {
            this.userPermissionsService.CurrentUserPermissions =
              response.UserPermissions;
            sessionStorage.setItem(
              'currentUserPermissions',
              JSON.stringify(response.UserPermissions)
            );

            (
              this.userPermissionsService as any
            ).currentUserPermissionsSource.next(response.UserPermissions);
          }
        }),
        catchError((error) => {
          console.error(
            'SyncCoordinator: Failed to sync configurations',
            error
          );
          return of(null);
        })
      );
  }

  /**
   * Error Handler
   */
  private handleError(error: any): Observable<null> {
    this.consecutiveErrors++;

    // Bei 401/403 sofort stoppen (User nicht mehr authentifiziert)
    if (error?.status === 401 || error?.status === 403) {
      console.warn('SyncCoordinator: Authentication error, stopping sync');
      this.stop();
      this.emitAuthErrorEvent();
      return of(null);
    }

    // Nur bei Debug-Mode oder kritischen Fehlern loggen
    if (this.debugMode || this.consecutiveErrors === 1) {
      console.warn(
        `SyncCoordinator: Sync check failed (${this.consecutiveErrors}/${this.MAX_ERRORS})`,
        error?.status || error?.message
      );
    }

    // Nicht mehr aufhören, nur seltener fragen. Bis 12.10.2026 stand hier `this.stop()`: Nach drei
    // Fehlern in Folge (Funkloch, Gerät im Hintergrund) erfuhr das Gerät bis zum nächsten
    // App-Neustart von keiner Einstellungsänderung mehr.
    this.skipTicks = skippedTicksAfterErrors(this.consecutiveErrors);
    if (this.consecutiveErrors === this.MAX_ERRORS) {
      console.warn('SyncCoordinator: repeated sync failures, backing off');
      this.emitErrorEvent();
    }

    return of(null);
  }

  /**
   * Emit global sync event für UI Components
   */
  private emitGlobalSyncEvent(changes: number[]): void {
    const changeTypes: string[] = [];

    if (changes.includes(ChangeTypeValue.CompanySettings)) {
      changeTypes.push('CompanySettings');
    }
    if (changes.includes(ChangeTypeValue.UserPermissions)) {
      changeTypes.push('UserPermissions');
    }

    window.dispatchEvent(
      new CustomEvent('configuration-synced', {
        detail: {
          changes: changeTypes,
          changeValues: changes,
          timestamp: new Date(),
          source: 'sync-coordinator',
        },
      })
    );
  }

  /**
   * Emit error event
   */
  private emitErrorEvent(): void {
    window.dispatchEvent(
      new CustomEvent('sync-error', {
        detail: {
          message: 'Configuration sync temporarily unavailable',
          timestamp: new Date(),
        },
      })
    );
  }

  /**
   * Emit auth error event
   */
  private emitAuthErrorEvent(): void {
    window.dispatchEvent(
      new CustomEvent('auth-error', {
        detail: {
          message: 'Authentication required',
          timestamp: new Date(),
        },
      })
    );
  }

  /**
   * Generiert eine Version/Hash für Company Settings
   */
  private getCompanySettingsVersion(): string {
    const settings = this.companySettingsService.CompanySettings;
    if (!settings) return '';

    const str = JSON.stringify(settings);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Generiert eine Version/Hash für User Permissions
   */
  private getUserPermissionsVersion(): string {
    const permissions = this.userPermissionsService.CurrentUserPermissions;
    if (!permissions) return '';

    const str = JSON.stringify(permissions);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Manueller Sync-Trigger (z.B. nach User-Aktionen)
   * WICHTIG: Nutzt skipBackgroundRefresh = true für UserPermissions
   */
  forceSync(): void {
    if (this.isSyncingSubject.value) {
      return;
    }

    if (!localStorage.getItem('token')) {
      return;
    }

    // Direkt beide Services updaten ohne auf den Check zu warten
    // Dies ist schneller für manuelle Syncs
    this.isSyncingSubject.next(true);

    try {
      this.companySettingsService.getCompanySettings(true);
      this.userPermissionsService.getCurrentUserPermissions(true);

      this.lastSyncTimeSubject.next(new Date());
      this.emitGlobalSyncEvent([1, 2]); // Beide Change Types
    } catch (error) {
      console.error('SyncCoordinator: Manual sync failed', error);
    } finally {
      setTimeout(() => {
        this.isSyncingSubject.next(false);
      }, 500);
    }
  }

  /**
   * Gibt den aktuellen Status zurück
   */
  getStatus(): {
    isActive: boolean;
    isSyncing: boolean;
    lastSync: Date | null;
    lastCheck: Date;
    errorCount: number;
  } {
    return {
      isActive: this.isActiveSubject.value,
      isSyncing: this.isSyncingSubject.value,
      lastSync: this.lastSyncTimeSubject.value,
      lastCheck: this.lastCheck,
      errorCount: this.consecutiveErrors,
    };
  }

  /**
   * Observable für Status-Monitoring
   */
  get isActive$(): Observable<boolean> {
    return this.isActiveSubject.asObservable();
  }

  get isSyncing$(): Observable<boolean> {
    return this.isSyncingSubject.asObservable();
  }

  get lastSyncTime$(): Observable<Date | null> {
    return this.lastSyncTimeSubject.asObservable();
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
